import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  notification,
  Space,
  Row,
  Col,
} from "antd";
import Joi from "joi";
import axios from "axios";
import config from "../../../../config/config";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: {
    container: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ header: [1, 2, 3, false] }],
      [{ align: [] }],
      ["link"], // ✅ ONLY link
      ["clean"],
    ],
  },
};

function EditFaq({ isVisible, handleClose, dbData, moduleName, base_url }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});

  const joiOptions = config.joiOptions;

  const validate = (value) => {
    const schemaObj = {
      title: Joi.string().required().label("Category Title"),
      faqs: Joi.array()
        .items(
          Joi.object({
            question: Joi.string().required().label("Question"),
            answer: Joi.string().required().label("Answer"),
          })
        )
        .required(),
    };

    const schema = Joi.object(schemaObj).options({ allowUnknown: true });
    const { error } = schema.validate(value, joiOptions);

    const errors = {};
    if (error) {
      error.details.forEach((field) => {
        errors[field.path.join(".")] = field.message;
      });
    }
    return errors;
  };

  const handleSubmit = async (values) => {
    const errors = validate(values);
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    try {
      const payload = {
        id: dbData._id,
        title: values.title,
        position: dbData.position,
        faqs: values.faqs,
      };

      const { data } = await axios.put(`${base_url}/update`, payload);

      if (data) {
        notification.success({ message: `${moduleName} updated successfully` });
        form.resetFields();
        handleClose();
      }
    } catch (err) {
      notification.error({
        message: "Update failed",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dbData) {
      form.setFieldsValue({
        title: dbData.title || "",
        faqs: (dbData.faqs || []).map((faq) => ({
          question: faq.question || "",
          answer: faq.answer || "",
        })),
      });
    }
  }, [dbData]);

  return (
    <Modal
      title={`Edit ${moduleName}`}
      visible={isVisible}
      onCancel={handleClose}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={loading}
      width={800}
      onOk={() => {
        form
          .validateFields()
          .then(handleSubmit)
          .catch(() => {});
      }}
    >
      <Form layout="vertical" form={form} name="edit_faq">
        <Form.Item
          label="Category Title"
          name="title"
          rules={[{ required: true, message: "Please enter category title" }]}
        >
          <Input placeholder="Enter category title" />
        </Form.Item>

        <Form.List name="faqs">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div
                  key={field.key}
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: 10,
                    marginBottom: 10,
                    borderRadius: 5,
                  }}
                >
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label={`Question ${index + 1}`}
                        name={[field.name, "question"]}
                        rules={[
                          { required: true, message: "Please enter question" },
                        ]}
                      >
                        <Input placeholder="Enter question" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        label={`Answer ${index + 1}`}
                        name={[field.name, "answer"]}
                        rules={[
                          { required: true, message: "Please enter answer" },
                        ]}
                        valuePropName="value"
                        getValueFromEvent={(content) => content}
                      >
                        <ReactQuill
                          theme="snow"
                          modules={quillModules}
                          placeholder="Enter answer"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button
                    danger
                    onClick={() => remove(field.name)}
                    disabled={fields.length === 1}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                style={{ width: "100%" }}
              >
                Add Another FAQ
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}

export default EditFaq;
